import React, { useMemo, useRef, useState } from "react"
import { useDispatch, useSelector } from "react-redux"
import { Button, Col, Form, Label, Row, Input } from "reactstrap"
import Select from "react-select"
import "flatpickr/dist/themes/material_blue.css"
import Flatpickr from "react-flatpickr"
import moment from "moment"
import {
  getProviderItemCategories,
  getProviderItems,
} from "store/provider-dashboard/provider-dashboard-action"
import { providerDashboardAction } from "store/provider-dashboard/provider-dashboard-slice"
import { isObject } from "lodash"
import { RECEIPTTYPE } from "constants/data"

const ProviderInFilter = ({ onAdvancedSearchSubmit, loading }) => {
  const expireFlatpickerfrom = useRef(null)
  const expireFlatpickerto = useRef(null)
  const receiptFlatpickerfrom = useRef(null)
  const receiptFlatpickerto = useRef(null)
  const [category, setCategory] = useState(null)
  const [item, setItem] = useState(null)
  const [project, setProject] = useState(null)
  const [currency, setCurrency] = useState(null)
  const [batchNumber, setBatchNumber] = useState("")
  const [receiptNumber, setReceiptNumber] = useState("")
  const [waybillNumber, setWaybillNumber] = useState("")
  const [shipment, setShipment] = useState(null)
  const [fundCode, setFundCode] = useState(null)
  const [commodityClassification, setCommodityClassification] = useState(null)
  const [receiptType, setReceiptType] = useState(null)
  const [type, setType] = useState(null)
  const [expireDate, setExpireDate] = useState({
    from: null,
    to: null,
  })
  const [receiptDate, setReceiptDate] = useState({
    from: null,
    to: null,
  })
  // Retrieve items data from Redux store
  const items = useSelector(state => state.providerDashboard.ProviderItems)

  // Retrieve projects data from Redux store
  const projects = useSelector(
    state => state.providerDashboard.providerProjects
  )

  // Retrieve categories data from Redux store
  const categories = useSelector(
    state => state.providerDashboard.ProviderItemCategoires
  )

  // Retrieve ips data from Redux store
  const ips = useSelector(state => state.providerDashboard.ProviderIps)

  // Retrieve currencies data from Redux store
  const currencies = useSelector(
    state => state.providerDashboard.ProviderCurrencies
  )

  const shipments = useSelector(
    state => state.providerDashboard.ProviderShipments
  )
  const fundCodes = useSelector(
    state => state.providerDashboard.ProviderFundCodes
  )
  const commodityClassifications = useSelector(
    state => state.providerDashboard.ProviderCommodityClassifications
  )

  // get filtered items based select box props needs
  const filteredItems = useMemo(
    () =>
      items?.data.map(item => ({
        value: item.id,
        label: `${item.unfp_item_name} ( ${item.item_name} )`,
      })),
    [items]
  )

  const dispatch = useDispatch()

  // Store filtered data and dispatch the action using onAdvancedSearchSubmit callback function
  const handleSubmit = () => {
    let data = {}
    if (item) {
      data.item = item.map(ite => (ite.value ? ite.value : ite))
    }

    data.project = isObject(project) ? project.value : null
    data.currency = isObject(currency) ? currency.value : null
    data.receiptType = isObject(receiptType) ? receiptType.value : null
    data.type = isObject(type) ? type.value : null
    data.batchNumber = batchNumber
    data.receiptNumber = receiptNumber
    data.waybillNumber = waybillNumber ? waybillNumber : null
    data.shipment = isObject(shipment) ? shipment.value : null
    data.fundCode = isObject(fundCode) ? fundCode.value : null
    data.commodityClassification = isObject(commodityClassification)
      ? commodityClassification.value
      : null
    data.category = isObject(category) ? category.value : null
    data.expireDate = {
      from: expireDate.from ? expireDate.from : null,
      to: expireDate.to ? expireDate.to : null,
    }
    data.receiptDate = {
      from: receiptDate.from ? receiptDate.from : null,
      to: receiptDate.to ? receiptDate.to : null,
    }
    onAdvancedSearchSubmit(data)
  }

  const onCommodityClassificationChangeHandler = value => {
    setCommodityClassification(value)
    setCategory(null)
    setItem(null)
    dispatch(providerDashboardAction.resetProviderItems())
    dispatch(getProviderItemCategories(value?.value ?? null))
  }

  // store Item Categories and fetch items based item category
  const onCategoryChangeHandler = cate => {
    setCategory(cate)
    dispatch(getProviderItems(cate.value))
  }

  // store multiple items based selected
  const onItemChangeHandler = item => {
    const check = item.some(ite => ite.value === "all")
    if (check) {
      setItem(filteredItems)
    } else {
      setItem(item)
    }
  }

  // store project value
  const onProjectChangeHandler = value => {
    setProject(value)
  }

  // store currency value
  const onCurrencyChangeHandler = value => {
    setCurrency(value)
  }

  // store recipt type value
  const onReceiptTypeChangeHandler = value => {
    setReceiptType(value)
  }

  // store type value
  const onTypeChangeHandler = value => {
    setType(value)
  }

  // clear search when clear button clicked
  const onClearHandler = () => {
    expireFlatpickerfrom.current.flatpickr.clear()
    expireFlatpickerto.current.flatpickr.clear()
    receiptFlatpickerfrom.current.flatpickr.clear()
    receiptFlatpickerto.current.flatpickr.clear()
    setCategory(null)
    setItem(null)
    setExpireDate({ from: null, to: null })
    setReceiptDate({ from: null, to: null })
    setProject(null)
    setCurrency(null)
    setBatchNumber("")
    setReceiptNumber("")
    setWaybillNumber("")
    setShipment(null)
    setFundCode(null)
    setCommodityClassification(null)
    onAdvancedSearchSubmit({})
    setReceiptType(null)
    setType(null)
    dispatch(providerDashboardAction.resetProviderItems())
  }

  return (
    <Form
      onSubmit={e => {
        e.preventDefault()
        handleSubmit()
        return false
      }}
    >
      <div className="mb-3">
        <Row>
          <Col>
            <Label className="form-label">Projects</Label>
            {projects?.data && (
              <Select
                options={projects?.data}
                value={project}
                onChange={onProjectChangeHandler}
              />
            )}
          </Col>
          <Col>
            <Label className="form-label">Currency</Label>
            {currencies?.data && (
              <Select
                options={currencies?.data}
                value={currency}
                onChange={onCurrencyChangeHandler}
              />
            )}
          </Col>
        </Row>
      </div>
      <div className="mb-3">
        <Row>
          <Col>
            <Label className="form-label">Type</Label>
            <Select
              options={[
                { value: "all", label: "All" },
                { value: "Receipt", label: "Receipt" },
                { value: "Other Source", label: "Other Source" },
              ]}
              value={type}
              onChange={onTypeChangeHandler}
            />
          </Col>
          <Col>
            <Label className="form-label">Batch Number</Label>
            <Input
              name="batchNumber"
              value={batchNumber}
              onChange={value => {
                setBatchNumber(value.target.value)
              }}
              placeholder="Batch Number"
              type="text"
            />
          </Col>
        </Row>
      </div>
      <div className="mb-3">
        <Row>
          <Col>
            <Label className="form-label">DS Number</Label>
            <Input
              name="receiptNumber"
              value={receiptNumber}
              onChange={value => {
                setReceiptNumber(value.target.value)
              }}
              placeholder="DS Number"
              type="text"
            />
          </Col>
          <Col>
            <Label className="form-label">Waybill Number</Label>
            <Input
              name="waybillNumber"
              value={waybillNumber}
              onChange={e => setWaybillNumber(e.target.value)}
              placeholder="Waybill Number"
              type="text"
            />
          </Col>
        </Row>
      </div>
      <div className="mb-3">
        <Row>
          <Col>
            <Label className="form-label">Shipment</Label>
            {shipments?.data && (
              <Select
                options={shipments?.data}
                value={shipment}
                onChange={value => setShipment(value)}
              />
            )}
          </Col>
          <Col>
            <Label className="form-label">Fund Code</Label>
            {fundCodes?.data && (
              <Select
                options={fundCodes?.data}
                value={fundCode}
                onChange={value => setFundCode(value)}
              />
            )}
          </Col>
        </Row>
      </div>
      <div className="mb-3">
        <Row>
          <Col>
            <Label className="form-label">Unit Classification</Label>
            {commodityClassifications?.data && (
              <Select
                options={commodityClassifications?.data}
                value={commodityClassification}
                onChange={onCommodityClassificationChangeHandler}
              />
            )}
          </Col>
          <Col></Col>
        </Row>
      </div>
      <div className="mb-3">
        <Row>
          <Col>
            <Label className="form-label">Item Categories</Label>
            {categories?.data && (
              <Select
                options={categories?.data}
                value={category || categories?.data[0]}
                onChange={onCategoryChangeHandler}
              />
            )}
          </Col>
          <Col>
            <Label className="form-label">Items</Label>
            {filteredItems && (
              <Select
                isMulti={true}
                options={[{ value: "all", label: "All" }, ...filteredItems]}
                value={item}
                onChange={onItemChangeHandler}
              />
            )}
          </Col>
        </Row>
      </div>

      <div className="mb-3 ">
        <Row>
          <Col>
            <Label className="form-label">Expire From Date</Label>
            <Flatpickr
              ref={expireFlatpickerfrom}
              className="form-control d-block"
              placeholder="dd M,yyyy"
              options={{
                altInput: true,
                altFormat: "Y-m-d",
                dateFormat: "Y-m-d",
              }}
              onChange={value => {
                setExpireDate(prev => {
                  return {
                    ...prev,
                    from: moment(value[0]).format("YYYY-MM-DD"),
                  }
                })
              }}
            />
          </Col>
          <Col>
            <Label className="form-label">Expire To Date</Label>
            <Flatpickr
              ref={expireFlatpickerto}
              className="form-control d-block"
              placeholder="dd M,yyyy"
              options={{
                altInput: true,
                altFormat: "Y-m-d",
                dateFormat: "Y-m-d",
              }}
              onChange={value => {
                setExpireDate(prev => {
                  return { ...prev, to: moment(value[0]).format("YYYY-MM-DD") }
                })
              }}
            />
          </Col>
        </Row>
      </div>

      <div className="mb-3">
        <Row>
          <Col>
            <Label className="form-label">Receipt Type</Label>
            <Select
              options={RECEIPTTYPE}
              value={receiptType}
              onChange={onReceiptTypeChangeHandler}
            />
          </Col>
        </Row>
      </div>

      <div className="mb-3 ">
        <Row>
          <Col>
            <Label className="form-label">Receipt From Date</Label>
            <Flatpickr
              ref={receiptFlatpickerfrom}
              className="form-control d-block"
              placeholder="dd M,yyyy"
              options={{
                altInput: true,
                altFormat: "Y-m-d",
                dateFormat: "Y-m-d",
              }}
              onChange={value => {
                setReceiptDate(prev => {
                  return {
                    ...prev,
                    from: moment(value[0]).format("YYYY-MM-DD"),
                  }
                })
              }}
            />
          </Col>
          <Col>
            <Label className="form-label">Receipt To Date</Label>
            <Flatpickr
              ref={receiptFlatpickerto}
              className="form-control d-block"
              placeholder="dd M,yyyy"
              options={{
                altInput: true,
                altFormat: "Y-m-d",
                dateFormat: "Y-m-d",
              }}
              onChange={value => {
                setReceiptDate(prev => {
                  return { ...prev, to: moment(value[0]).format("YYYY-MM-DD") }
                })
              }}
            />
          </Col>
        </Row>
      </div>

      <div className="d-flex flex-wrap gap-2 mb-4">
        <Button
          type="submit"
          className="btn btn-sm btn-primary"
          color="primary"
          disabled={loading}
        >
          {loading ? "Please Wait..." : "Search"}
        </Button>
        <Button
          onClick={onClearHandler}
          className="btn btn-sm btn-danger"
          color="primary"
        >
          Clear
        </Button>
      </div>
    </Form>
  )
}

export default ProviderInFilter
